import type { ReactNode } from 'react';
import type { SocialConnection } from '@mg/shared';
import { AlertCircle, CheckCircle2, ExternalLink } from 'lucide-react';
import { Modal } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';
import { formatRelative } from '../../lib/display';
import { PLATFORM_META } from './platforms';
import { reconnectNeeded } from './reconnect';
import { formatSignInEnd } from './signInExpiry';

/** Read-only details for a connected social account. */
export function ConnectionDetailsModal({
  conn,
  onClose,
}: {
  conn: SocialConnection | null;
  onClose: () => void;
}) {
  const meta = conn ? PLATFORM_META[conn.platform] : null;
  const Icon = meta?.Icon;
  const scopes = (conn?.scopes ?? '')
    .split(/[,\s]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  const connectedOn = conn?.connectedAt
    ? new Date(conn.connectedAt).toLocaleDateString(undefined, { dateStyle: 'medium' })
    : null;

  return (
    <Modal
      open={conn != null}
      onClose={onClose}
      title="Connection details"
      footer={
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      {conn && meta && Icon && (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${meta.tile}`}>
              <Icon className="h-5 w-5" aria-hidden />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-ink">{meta.label}</span>
                {/* The card's own red badge (the same test), so the two never disagree. */}
                {reconnectNeeded(conn) ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-red-50 dark:bg-error-dark-tint px-2 py-0.5 text-xs font-medium text-red-700 dark:text-error-dark">
                    <AlertCircle className="h-3.5 w-3.5" aria-hidden /> Reconnect needed
                  </span>
                ) : conn.connected ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-success-tint dark:bg-success-dark-tint px-2 py-0.5 text-xs font-medium text-success dark:text-success-dark">
                    <CheckCircle2 className="h-3.5 w-3.5" aria-hidden /> Connected
                  </span>
                ) : null}
              </div>
              {conn.accountName && (
                <div className="truncate text-sm text-ink-label">{conn.accountName}</div>
              )}
            </div>
          </div>

          <dl className="divide-y divide-line-soft rounded-xl border border-line-soft">
            <Row label="Account">{conn.accountName ?? '—'}</Row>
            {conn.accountHandle && conn.accountHandle !== conn.accountName && (
              <Row label="Handle">{conn.accountHandle}</Row>
            )}
            {conn.accountUrl && (
              <Row label="Profile">
                <a
                  href={conn.accountUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="focus-ring inline-flex items-center gap-1 break-all font-medium text-link hover:text-action-hover hover:underline"
                >
                  {conn.accountUrl}
                  <ExternalLink className="h-3.5 w-3.5 shrink-0" aria-hidden />
                </a>
              </Row>
            )}
            {conn.externalId && (
              <Row label="Page / profile ID">
                <code className="break-all text-xs text-ink-strong">{conn.externalId}</code>
              </Row>
            )}
            {conn.appId && (
              <Row label="App / client ID">
                <code className="break-all text-xs text-ink-strong">{conn.appId}</code>
              </Row>
            )}
            {scopes.length > 0 && (
              <Row label="Permissions">
                <div className="flex flex-wrap gap-1.5">
                  {scopes.map((s) => (
                    <span
                      key={s}
                      className="inline-flex items-center rounded-full bg-chip px-2 py-0.5 text-xs font-medium text-ink-label"
                    >
                      {s}
                    </span>
                  ))}
                </div>
              </Row>
            )}
            {connectedOn && <Row label="Connected">{connectedOn}</Row>}
            {/* Only for a sign-in that ends: a Facebook Page's does not, and X's is renewed hourly. Once
                its end has passed it has ended, whatever renewed it: only a Reconnect brings it back. */}
            {conn.signInExpiresAt &&
              (new Date(conn.signInExpiresAt).getTime() <= Date.now() ? (
                <Row label="Sign-in ended">
                  {formatSignInEnd(conn.signInExpiresAt)} · reconnect to publish to it again
                </Row>
              ) : (
                <Row label="Sign-in ends">
                  {formatSignInEnd(conn.signInExpiresAt)} ·{' '}
                  {conn.renewsAutomatically ? 'renews automatically' : 'reconnect before then'}
                </Row>
              ))}
            <Row label="Last synced">{formatRelative(conn.lastSyncedAt ?? null)}</Row>
          </dl>
        </div>
      )}
    </Modal>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 px-4 py-2.5">
      <dt className="shrink-0 text-sm text-ink-muted">{label}</dt>
      <dd className="min-w-0 text-right text-sm text-ink">{children}</dd>
    </div>
  );
}
